import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { useToast } from '../../../hooks/useToast';
import useAuthHook from '../../../hooks/useAuth.hook';
import ApiWrapper from '../../../hooks/useApi.hook';
import { DATE_TIME_FORMAT } from '../../../constants/date.contant';
import {
  deleteCalendarEventThunk,
  getPersonInterviewsThunk,
} from '../../../redux/Calendar/Calendar.thunk';
import {
  removeInterviewLocalAction,
  selectPersonInterviews,
  selectPersonInterviewsFetched,
  selectPersonInterviewsLoading,
} from '../../../redux/Calendar/Calendar.slice';
import { formatDisplayDateTime } from '../../../redux/Calendar/Calendar.mapper';
import {
  getCandidateSelected,
  getCandidateSelectedForPreview,
} from '../../../redux/Candidate/Candidate.slice';
import { PersonRecord } from '../../../redux/Candidate/Record/Record.type';
import { ALERT_ACTION_ENUM } from '../../../types/action.types';
import { UserPermissions } from '../../../types/userPermissions.types';
import { checkUserRequiredPermissions } from '../../../utils/getUserRolesByToken';
import GoogleCalendarEventDeleteAlertComponent from '../../Alert/Calendar/GoogleCalendarEventDeleteAlert/GoogleCalendarEventDeleteAlert.component';
import ButtonComponent from '../../Button.component';
import GoogleCalendarEventModalComponent from '../../Modal/Calendar/GoogleCalendarEventModal.component';

export interface GoogleCalendarInterviewBlockProps {
  record: PersonRecord;
}

const StyledInterview = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
`;

const StyledInterviewDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const StyledInterviewActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const GoogleCalendarInterviewBlockComponent = ({
  record,
}: GoogleCalendarInterviewBlockProps) => {
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const { successToast, errorToast } = useToast();
  const { user } = useAuthHook();
  const { t } = useTranslation();

  const canViewList = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_CANDIDATE_INTERVIEWS_HISTORY,
  ]);
  const canEdit = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_GOOGLE_MEET_INTERVIEW,
  ]);
  const canDelete = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_GOOGLE_MEET_INTERVIEW,
  ]);

  const interviewsByPerson = useAppSelector(
    selectPersonInterviews(record.personId),
  );
  const isInterviewsLoading = useAppSelector(
    selectPersonInterviewsLoading(record.personId),
  );
  const isInterviewsFetched = useAppSelector(
    selectPersonInterviewsFetched(record.personId),
  );
  const interviews = useMemo(
    () =>
      interviewsByPerson.filter(
        interview => interview.personRecordId === record.id,
      ),
    [interviewsByPerson, record.id],
  );
  const candidateSelected = useAppSelector(getCandidateSelected);
  const candidateSelectedForPreview = useAppSelector(
    getCandidateSelectedForPreview,
  );
  const formCandidate =
    candidateSelected.item?.id === record.personId
      ? candidateSelected.item
      : candidateSelectedForPreview.item?.id === record.personId
        ? candidateSelectedForPreview.item
        : undefined;

  useEffect(() => {
    if (!canViewList || isInterviewsLoading || isInterviewsFetched) return;
    dispatch(getPersonInterviewsThunk(record.personId));
  }, [
    canViewList,
    isInterviewsLoading,
    isInterviewsFetched,
    dispatch,
    record.personId,
  ]);

  const handleDelete = (interviewId: number) => {
    openModal(GoogleCalendarEventDeleteAlertComponent, {
      handleConfirm: async (confirm?: ALERT_ACTION_ENUM) => {
        if (confirm !== ALERT_ACTION_ENUM.YES) return;

        const result = await ApiWrapper(deleteCalendarEventThunk(interviewId));
        if (result === false) {
          errorToast(t('calendarEvent.deleteErrorMessage'));
          return;
        }

        successToast(t('calendarEvent.deleteSuccessMessage'));
        dispatch(
          removeInterviewLocalAction({
            personId: record.personId,
            interviewId,
          }),
        );
      },
    });
  };

  if (!canViewList || !interviews.length) return null;

  return (
    <React.Fragment>
      {interviews.map(interview => (
        <StyledInterview key={interview.id}>
          <StyledInterviewDetails>
            <div>{interview.title}</div>
            <div>
              {formatDisplayDateTime(interview.startTime, DATE_TIME_FORMAT)} —{' '}
              {formatDisplayDateTime(interview.endTime, DATE_TIME_FORMAT)}
            </div>
            {interview.meetLink && (
              <a href={interview.meetLink} target="_blank" rel="noreferrer">
                {t('calendarEvent.openMeet')}
              </a>
            )}
          </StyledInterviewDetails>
          <StyledInterviewActions>
            {canEdit && interview.googleEventId && (
              <ButtonComponent
                btnSize="small"
                handleClick={() =>
                  openModal(GoogleCalendarEventModalComponent, {
                    mode: 'edit',
                    googleEventId: interview.googleEventId,
                    interviewId: interview.id,
                    eventStatusCode: record.status?.code,
                    record,
                    candidateName: formCandidate?.fullName,
                    candidateEmail: formCandidate?.email,
                    candidateAttachments: formCandidate?.attachments,
                  })
                }
              >
                {t('calendarEvent.edit')}
              </ButtonComponent>
            )}
            {canDelete && (
              <ButtonComponent
                btnSize="small"
                handleClick={() => handleDelete(interview.id)}
              >
                {t('calendarEvent.delete')}
              </ButtonComponent>
            )}
          </StyledInterviewActions>
        </StyledInterview>
      ))}
    </React.Fragment>
  );
};

export default GoogleCalendarInterviewBlockComponent;
